<template>
  <ol :class="$style['list']">
    <li v-for="item in items" :key="item.to">
      <NuxtLink :to="item.to" :class="$style['item']">
        <span :class="$style['item__photo']">
          <NuxtImg v-if="item.image" :src="item.image" width="480" height="360" format="webp" loading="lazy" alt="" />
          <SiteMark v-else />
        </span>
        <span :class="$style['item__text']">
          <time v-if="item.date" :datetime="item.date" :class="$style['item__meta']">{{ formatDate(item.date) }}</time>
          <span v-else-if="item.meta" :class="$style['item__meta']">{{ item.meta }}</span>
          <span :class="$style['item__title']">{{ item.title }}</span>
          <span v-if="item.text" :class="$style['item__text-line']">{{ item.text }}</span>
        </span>
      </NuxtLink>
    </li>
  </ol>
</template>
<script setup lang="ts">
// Pages as rows: photo, title and a line of text. Hovering floods a row amber, like the dates on the homepage.
interface PageListItem {
  to: string
  title: string
  image?: string
  date?: string
  meta?: string
  text?: string
}

defineProps<{ items: Array<PageListItem> }>()

const formatDate = (date: string) => new Date(date).toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })
</script>
<style module>
.list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.item {
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: clamp(7rem, 4rem + 12vw, 18rem) minmax(0, 1fr);
  align-items: center;
  gap: var(--spacing-l) clamp(1.25rem, 0.5rem + 3vw, 3.5rem);
  margin-inline: calc(-1 * var(--spacing-l));
  padding: clamp(1rem, 0.6rem + 1.2vw, 1.75rem) var(--spacing-l);
  border-top: var(--stage-hairline) solid var(--stage-line);
  color: var(--stage-white);
  text-decoration: none;
  transition: color 250ms;
}

.item::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  background: var(--primary-color);
  transform: scaleY(0);
  transform-origin: 50% 100%;
  transition: transform 450ms var(--stage-ease);
}

.item:hover,
.item:focus-visible {
  color: var(--stage-black);
}

.item:hover::before,
.item:focus-visible::before {
  transform: scaleY(1);
}

.list > li:last-child > .item {
  border-bottom: var(--stage-hairline) solid var(--stage-line);
}

.item__photo {
  display: block;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  background: var(--background-raised);
}

.item__photo svg {
  display: block;
  width: 30%;
  height: 100%;
  margin-inline: auto;
  color: var(--stage-line);
}

.item__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 600ms var(--stage-ease);
}

.item:hover .item__photo img {
  transform: scale(1.05);
}

.item__text {
  display: grid;
  gap: var(--spacing);
}

.item__meta {
  font-size: var(--font-size-small);
  opacity: 0.7;
}

.item__title {
  max-width: 24ch;
  font-family: var(--font-family-heading);
  font-weight: var(--font-weight-bold);
  font-size: clamp(1.25rem, 0.95rem + 0.9vw, 1.875rem);
  line-height: 0.95;
  letter-spacing: -0.025em;
  text-transform: uppercase;
  text-wrap: balance;
}

.item__text-line {
  max-width: var(--stage-measure);
  line-height: 1.5;
  opacity: 0.75;
}

@media (width <= 40rem) {
  .item {
    align-items: start;
  }

  .item__text-line {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .item,
  .item::before,
  .item__photo img {
    transition: none;
  }
}
</style>
